import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Coins } from "lucide-react";
import api, { apiErr } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Register() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", password: "", referral_code: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const ref = params.get("ref");
    if (ref) setForm((f) => ({ ...f, referral_code: ref }));
  }, [params]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.referral_code) delete payload.referral_code;
      const { data } = await api.post("/auth/register", payload);
      login(data.token, data.user);
      toast.success("Account created! Welcome to ERAN USD");
      navigate("/dashboard");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md card-surface rounded-3xl p-8 glow-emerald">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <Coins className="h-7 w-7 text-emerald-400" />
          <span className="text-xl font-extrabold">ERAN <span className="text-emerald-400">USD</span></span>
        </Link>
        <h1 className="text-2xl font-bold mb-1 text-center">Create Account</h1>
        <p className="text-sm text-slate-400 text-center mb-6">Get your unique USDT deposit address instantly</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-slate-300">Full Name</Label>
            <Input data-testid="register-name-input" required value={form.name} onChange={set("name")}
              className="mt-1.5 bg-white/5 border-slate-700" placeholder="Enter your full name" />
          </div>
          <div>
            <Label className="text-slate-300">Email</Label>
            <Input data-testid="register-email-input" type="email" required value={form.email} onChange={set("email")}
              className="mt-1.5 bg-white/5 border-slate-700" placeholder="you@example.com" />
          </div>
          <div>
            <Label className="text-slate-300">Password</Label>
            <Input data-testid="register-password-input" type="password" required value={form.password} onChange={set("password")}
              className="mt-1.5 bg-white/5 border-slate-700" placeholder="min 6 characters" />
          </div>
          <div>
            <Label className="text-slate-300">Referral Code <span className="text-slate-500">(optional)</span></Label>
            <Input data-testid="register-referral-input" value={form.referral_code} onChange={set("referral_code")}
              className="mt-1.5 bg-white/5 border-slate-700 font-mono" placeholder="ERNXXXXXX" />
          </div>
          <Button data-testid="register-submit-button" type="submit" disabled={loading}
            className="w-full rounded-full bg-emerald-500 hover:bg-emerald-600 text-white h-11">
            {loading ? "Creating…" : "Register"}
          </Button>
        </form>
        <p className="text-sm text-slate-400 text-center mt-6">
          Already have account? <Link to="/login" data-testid="to-login-link" className="text-emerald-400 hover:underline">Login</Link>
        </p>
      </div>
    </div>
  );
}
